import { useTranslation } from 'react-i18next';
import useGroupByBuyerCurrency from '@/enterprise/components/InvoiceCentral/useGroupByBuyerCurrency';
import { formatBuyerNameJsx } from '@/enterprise/components/InvoiceCentral/utils';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BuyerLogo from '../BuyerLogo';
import Widget, { WidgetSkeleton } from './Widget';

const InvoiceCentralWidgetComponent = () => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { data: takerMarkets } = useTakerMarkets();
  const buyers = useGroupByBuyerCurrency({ takerMarkets, includeAwardedInvoices: false });

  // display max of 5 buyer-currency groups
  const topBuyers = buyers.slice(0, 5);

  // total invoice count across all buyer-currency groups
  let totalInvoiceCount = 0;
  for (const buyer of buyers) {
    for (const market of buyer.takerMarkets) {
      totalInvoiceCount += market.totalInvoiceCount;
    }
  }

  const items = topBuyers.map((buyer) => {
    return {
      key: `${buyer.makerOrganizationUuid}-${buyer.currency}`,
      content: (
        <div className="flex items-center gap-2">
          <div>
            <BuyerLogo buyerLogoUrl={buyer.makerOrganizationLogo} buyerName={buyer.makerOrganizationName} size="lg" />
          </div>
          <div className="truncate">
            <div className="truncate">{formatBuyerNameJsx({ buyerToFormat: buyer, buyers })}</div>
            <div className="truncate font-serif text-2xl">
              {asCurrency(buyer.stats.totalInvoiceAmount, buyer.currency)}
            </div>
          </div>
        </div>
      ),
      link: asEnterpriseLink(`invoice-central/${buyer.makerOrganizationUuid}/${buyer.currency}/eligible`),
    };
  });

  return (
    <Widget
      items={items}
      subtitle={t('core.totalInvoicesCount', {
        count: totalInvoiceCount,
        invoiceCount: asNumber({ value: totalInvoiceCount }),
      })}
      title={t('invoiceCentral.title')}
      viewAllLink={asEnterpriseLink('invoice-central')}
      widgetType="invoice-central"
      zeroItemsMessage={t('homepage.invoiceCentralWidgetZeroItemsMessage')}
    />
  );
};

const InvoiceCentralWidget = () => (
  <QueryBoundaries LoadingComponent={() => <WidgetSkeleton />}>
    <InvoiceCentralWidgetComponent />
  </QueryBoundaries>
);

export default InvoiceCentralWidget;
